import React, { useEffect, useState } from "react";
import type { FC, PropsWithChildren } from "react";
import { ModalForm, Modal, Button } from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";
import SkeletonLoader from "../../Skeletons/IntakeFormSkeleton";
import InputWrapper from "../../components/Modal/InputWrapper";
import SliderBar from "../../components/Modal/SlideBar";
import TextAreaField from "../../components/Modal/TextAreaField";
import RadioButton from "../../components/Modal/RadioButton";
import CheckboxInput from "../../components/Modal/CheckBoxInput";
import PatientStyle from "../../styles/Patient.module.css";
import { IPatient } from "../../hooks/patients/index.type";
import { formatLabel } from "../../utils/formatlabel";
import { useFetchCarePlan } from "../../hooks/patients";
import InputField from "../../components/Modal/InputField";

type AddEditModalProps = PropsWithChildren<{
  show: boolean;
  onClose: () => void;
  patient: IPatient;
  transmissionTemplate: { _id: string; name: string };
}>;

const CarePlanModal: FC<AddEditModalProps> = (props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [formState, setFormState] = useState<{ [key: string]: any }>({});

  const { getCarePlan, carePlan, carePlanLoading } = useFetchCarePlan({
    templateId: props.transmissionTemplate._id,
    patientId: props.patient._id,
  });

  useEffect(() => {
    getCarePlan();
  }, []);

  const renderQuestion = (record: any, idx: number, categoryName?: string) => {
    const answerType = record.answerType;
    const options = record.option || [];

    const questionWithSerialNumber = `${idx + 1}. ${record.text}`;

    return (
      <div
        key={record._id || record.questionId || idx}
        style={{ marginBottom: "1rem" }}
      >
        {answerType === "single_option" && (
          <InputWrapper
            subTitle={questionWithSerialNumber}
            title={categoryName && idx === 0 ? categoryName : undefined}
          >
            <div
              style={{
                display: "flex",
                flexDirection: options.length < 2 ? "row" : "column",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {options.map((opt: any) => (
                <div key={opt.key}>
                  <RadioButton
                    hideRadio={true}
                    label={opt.name}
                    checked={formState[record.questionId] === opt.name}
                  />
                  {opt.textboxRequired === "yes" &&
                    formState[record.questionId] === opt.name && (
                      <TextAreaField
                        rows={2}
                        placeholder="Please specify"
                        value={
                          formState[
                            `${record.questionId}_${opt.name}_textbox`
                          ] || ""
                        }
                      />
                    )}
                </div>
              ))}
            </div>
          </InputWrapper>
        )}

        {answerType === "multiple_option" && (
          <InputWrapper
            subTitle={questionWithSerialNumber}
            title={categoryName && idx === 0 ? categoryName : undefined}
          >
            <div className={PatientStyle.checkbox_div_1}>
              {options.map((opt: any) => (
                <div key={opt.key}>
                  <CheckboxInput
                    hideCheckbox={true}
                    label={opt.name}
                    checked={(formState[record.questionId] || []).includes(
                      opt.name
                    )}
                  />
                  {opt.textboxRequired === "yes" &&
                    (formState[record.questionId] || []).includes(opt.name) && (
                      <TextAreaField
                        rows={2}
                        placeholder="Please specify"
                        value={
                          formState[
                            `${record.questionId}_${opt.name}_textbox`
                          ] || ""
                        }
                      />
                    )}
                </div>
              ))}
            </div>
          </InputWrapper>
        )}

        {answerType === "textarea" && (
          <InputWrapper
            subTitle={questionWithSerialNumber}
            title={categoryName && idx === 0 ? categoryName : undefined}
          >
            <TextAreaField
              rows={2}
              value={formState[record.questionId] || ""}
            />
          </InputWrapper>
        )}

        {answerType === "scale" && (
          <InputWrapper
            subTitle={questionWithSerialNumber}
            title={categoryName && idx === 0 ? categoryName : undefined}
          >
            <SliderBar
              scale
              value={formState[record.questionId] || 0}
              customLabel
              min={parseInt(record.scale.min.key)}
              max={parseInt(record.scale.max.key)}
              leftLabel={record.scale.min.name}
              rightLabel={record.scale.max.name}
            />
          </InputWrapper>
        )}
      </div>
    );
  };

  return (
    <Modal show={props.show} modalClassName={PatientStyle.customEnrollModal}>
      <div className={PatientStyle.modalHeaderContainer}>
        <ModalHeading
          heading={carePlan?.data?.templateName}
          customClass={[PatientStyle.customEnrollHeading].join(" ")}
          customCloseClass={PatientStyle.close_btn}
          closeIcon="close-cross"
          onClose={props.onClose}
        />
        {carePlan?.data?.planName && (
          <p className={PatientStyle.planName}>
            {carePlan.data.planName}
          </p>
        )}
      </div>
      

      {/* <button
        onClick={props.onClose}
        className={PatientStyle.modal_close_button}
        aria-label="Close"
      >
        &times;
      </button> */}

      {/* Search Input Field */}
      <div style={{ 
        padding: "1.5rem 2rem", 
      }}>
        <InputField
          icon="search"
          type="text"
          placeholder="Search questions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          customClass={PatientStyle.form_input_field}
        />
      </div>

      {carePlanLoading ? (
        <SkeletonLoader />
      ) : (
        <ModalForm
          customClass={PatientStyle.enroll_ModalForm}
          id="confirm-form"
        >
          <div
            className={PatientStyle.enroll_ModalForm_inner}
            style={{ marginBottom: "1rem" }}
          >
            {(() => {
              if (!carePlan || !carePlan.data || !carePlan.data.data) {
                return (
                  <p
                    style={{
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    {searchTerm
                      ? "No questions match your search."
                      : "No questions available in this care plan."}
                  </p>
                );
              }

              let sourceData = carePlan?.data?.data;
              let filteredData = sourceData;

              if (searchTerm) {
                if (carePlan?.data?.questionGroupedByCategory === "yes") {
                  filteredData = (sourceData as any[])
                    .map((categoryData: any) => {
                      const categoryNameMatches = categoryData.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase());
                      if (categoryNameMatches) {
                        return categoryData; 
                      }
                     
                      const filteredRecords = categoryData.records.filter(
                        (record: any) =>
                          record.text
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())
                      );
                      if (filteredRecords.length > 0) {
                        return { ...categoryData, records: filteredRecords };
                      }
                      return null; 
                    })
                    .filter(
                      (categoryData: any) => categoryData !== null
                    ) as any[];
                } else {
                  filteredData = (sourceData as any[]).filter((record: any) =>
                    record.text.toLowerCase().includes(searchTerm.toLowerCase())
                  );
                }
              }

              if (filteredData.length === 0) {
                if (searchTerm) {
                  return (
                    <p
                      style={{
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      No questions match your search.
                    </p>
                  );
                } else {
                  return (
                    <p
                      style={{
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      No questions available.
                    </p>
                  );
                }
              }

              return carePlan?.data?.questionGroupedByCategory === "yes" ? (
                (filteredData as any[]).map(
                  (categoryData: any, categoryIdx: number) => (
                    <div
                      key={categoryData._id || `category-${categoryIdx}`}
                      className={PatientStyle.custom_content}
                    >
                      {[...categoryData.records]
                        .sort((a, b) => {
                          const seqA = a.sequence || 0;
                          const seqB = b.sequence || 0;
                          return seqA - seqB;
                        })
                        .map((record: any, idx: number) =>
                          renderQuestion(record, idx, categoryData.name)
                        )}
                    </div>
                  )
                )
              ) : (
                <div className={PatientStyle.custom_content}>
                  {[...(filteredData as any[])]
                    .sort((a, b) => {
                      const seqA = a.sequence || 0;
                      const seqB = b.sequence || 0;
                      return seqA - seqB;
                    })
                    .map((record: any, idx: number) =>
                      renderQuestion(record, idx)
                    )}
                </div>
              );
            })()}
          </div>
          <div className={PatientStyle.custom_btn_box}>
            <Button
              label="Cancel"
              icon="icon-close-cross"
              span={false}
              iconStyle={{ height: "1.5rem", width: "1.5rem" }}
              customClass={PatientStyle.cancel_btn}
              onClick={props.onClose}
              form="confirm-form"
            />
          </div>
        </ModalForm>
      )}
    </Modal>
  );
};

export default CarePlanModal;
